import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';

const CustomerProfileModal = ({
  show,
  onHide,
  currentUser,
  onUpdateSuccess
}) => {
  const [formData, setFormData] = useState({
    d_razon: '',
    d_email: '',
    d_whatsapp: '',
    n_telef: '',
    n_celul: '',
    d_domic: '',
    d_local: '',
    c_posta: '',
    d_passnu: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Cargar datos del usuario al abrir el modal
  useEffect(() => {
    if (show && currentUser) {
      setFormData({
        d_razon: currentUser.d_razon || currentUser.nombre || '',
        d_email: currentUser.d_email || currentUser.email || '',
        d_whatsapp: currentUser.d_whatsapp || currentUser.whatsapp || '',
        n_telef: currentUser.n_telef || '',
        n_celul: currentUser.n_celul || '',
        d_domic: currentUser.d_domic || '',
        d_local: currentUser.d_local || '',
        c_posta: currentUser.c_posta || '',
        d_passnu: currentUser.d_passnu || ''
      });

      // Si tiene ID, obtener datos frescos del backend
      if (currentUser.id) {
        setIsLoading(true);
        axios.get(`/api/clientes-tienda/${currentUser.id}/`)
          .then(res => {
            if (res.data) {
              setFormData({
                d_razon: res.data.d_razon || '',
                d_email: res.data.d_email || '',
                d_whatsapp: res.data.d_whatsapp || '',
                n_telef: res.data.n_telef || '',
                n_celul: res.data.n_celul || '',
                d_domic: res.data.d_domic || '',
                d_local: res.data.d_local || '',
                c_posta: res.data.c_posta || '',
                d_passnu: res.data.d_passnu || ''
              });
            }
          })
          .catch(err => {
            console.warn('Error refrescando datos del cliente:', err);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [show, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.d_razon.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Nombre requerido',
        text: 'Por favor ingresá tu nombre o razón social.'
      });
      return;
    }

    if (!currentUser?.id) {
      Swal.fire({
        icon: 'info',
        title: 'Usuario de Sistema',
        text: 'Los usuarios del sistema ERP deben solicitar cambios de perfil al administrador.'
      });
      onHide();
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        d_razon: formData.d_razon.trim(),
        d_whatsapp: formData.d_whatsapp.trim(),
        n_telef: formData.n_telef.trim(),
        n_celul: formData.n_celul.trim(),
        d_domic: formData.d_domic.trim(),
        d_local: formData.d_local.trim(),
        c_posta: formData.c_posta.trim(),
        ...(formData.d_passnu ? { d_passnu: formData.d_passnu.trim() } : {})
      };

      const res = await axios.patch(`/api/clientes-tienda/${currentUser.id}/`, payload);
      const updatedUser = {
        ...currentUser,
        ...(res.data || {}),
        d_razon: payload.d_razon,
        d_whatsapp: payload.d_whatsapp,
        n_telef: payload.n_telef,
        n_celul: payload.n_celul,
        d_domic: payload.d_domic,
        d_local: payload.d_local,
        c_posta: payload.c_posta
      };

      localStorage.setItem('sigecom_ecom_customer', JSON.stringify(updatedUser));
      localStorage.setItem('sigecom_ecom_last_activity', Date.now().toString());

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: '¡Tus datos fueron actualizados correctamente!',
        showConfirmButton: false,
        timer: 2500
      });

      if (onUpdateSuccess) {
        onUpdateSuccess(updatedUser);
      }
      onHide();
    } catch (err) {
      console.error('Error actualizando perfil:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron actualizar tus datos. Por favor intentá nuevamente.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const currentListaNombre = currentUser?.nombre_lista || (currentUser?.n_lista ? `Lista ${currentUser.n_lista}` : 'Lista 1 - Minorista');

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="customer-profile-modal"
    >
      <Modal.Header closeButton className="bg-light border-0 py-3 px-4">
        <Modal.Title className="fw-bold fs-5 text-dark d-flex align-items-center gap-2">
          <div className="bg-primary text-white rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', fontSize: '1rem' }}>
            <i className="bi bi-person-gear"></i>
          </div>
          <div>
            <div className="fw-black text-dark mb-0 fs-5">Mis Datos y Perfil Comercial</div>
            <div className="text-muted small fw-normal" style={{ fontSize: '0.78rem' }}>
              Actualizá tu información de contacto, domicilio de entrega y contraseña.
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="text-muted small mt-2">Cargando tus datos...</div>
            </div>
          ) : (
            <>
              {/* Tarjeta de Lista de Precios Asignada */}
              <div className="p-3 bg-primary-subtle border border-primary-subtle rounded-4 mb-4 d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-primary text-white rounded-circle p-2.5 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                    <i className="bi bi-tags-fill fs-5"></i>
                  </div>
                  <div>
                    <div className="small text-muted fw-semibold">Lista de Precios Asignada:</div>
                    <div className="fw-black text-primary fs-5">{currentListaNombre}</div>
                    <div className="text-muted text-xs">Los precios del catálogo y tus descargas se calculan con esta lista.</div>
                  </div>
                </div>
                <span className="badge bg-white text-primary border border-primary-subtle rounded-pill px-3 py-1.5 small fw-bold">
                  ● Cuenta Activa
                </span>
              </div>

              {/* SECCIÓN 1: DATOS PERSONALES Y CONTACTO */}
              <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 d-flex align-items-center gap-2">
                <i className="bi bi-person-lines-fill text-primary"></i>
                <span>Datos de Contacto</span>
              </h6>

              <div className="row g-3 mb-4">
                {/* Nombre / Razón Social */}
                <div className="col-12 col-md-6">
                  <Form.Group controlId="profileRazon">
                    <Form.Label className="small fw-bold text-muted mb-1">Nombre / Razón Social *</Form.Label>
                    <Form.Control
                      type="text"
                      name="d_razon"
                      value={formData.d_razon}
                      onChange={handleChange}
                      placeholder="Tu nombre completo o de empresa"
                      required
                    />
                  </Form.Group>
                </div>

                {/* Email (Solo lectura para identificación) */}
                <div className="col-12 col-md-6">
                  <Form.Group controlId="profileEmail">
                    <Form.Label className="small fw-bold text-muted mb-1">Correo Electrónico (Usuario)</Form.Label>
                    <Form.Control
                      type="email"
                      name="d_email"
                      value={formData.d_email}
                      disabled
                      className="bg-light text-muted font-monospace"
                    />
                    <Form.Text className="text-muted text-2xs">Identificador único de tu cuenta.</Form.Text>
                  </Form.Group>
                </div>

                {/* WhatsApp */}
                <div className="col-12 col-md-4">
                  <Form.Group controlId="profileWhatsapp">
                    <Form.Label className="small fw-bold text-muted mb-1">WhatsApp Comercial</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-light text-success fw-bold">
                        <i className="bi bi-whatsapp"></i>
                      </span>
                      <Form.Control
                        type="text"
                        name="d_whatsapp"
                        value={formData.d_whatsapp}
                        onChange={handleChange}
                        placeholder="1122971930"
                      />
                    </div>
                  </Form.Group>
                </div>

                {/* Teléfono Fijo */}
                <div className="col-12 col-md-4">
                  <Form.Group controlId="profileTelef">
                    <Form.Label className="small fw-bold text-muted mb-1">Teléfono Fijo / Empresa</Form.Label>
                    <Form.Control
                      type="text"
                      name="n_telef"
                      value={formData.n_telef}
                      onChange={handleChange}
                      placeholder="(011) 4247-6622"
                    />
                  </Form.Group>
                </div>

                {/* Celular Alternativo */}
                <div className="col-12 col-md-4">
                  <Form.Group controlId="profileCelul">
                    <Form.Label className="small fw-bold text-muted mb-1">Celular Alternativo</Form.Label>
                    <Form.Control
                      type="text"
                      name="n_celul"
                      value={formData.n_celul}
                      onChange={handleChange}
                      placeholder="1198765432"
                    />
                  </Form.Group>
                </div>
              </div>

              {/* SECCIÓN 2: DOMICILIO DE ENTREGA Y FACTURACIÓN */}
              <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 d-flex align-items-center gap-2">
                <i className="bi bi-geo-alt-fill text-danger"></i>
                <span>Domicilio de Entrega / Despacho</span>
              </h6>

              <div className="row g-3 mb-4">
                {/* Dirección */}
                <div className="col-12 col-md-5">
                  <Form.Group controlId="profileDomic">
                    <Form.Label className="small fw-bold text-muted mb-1">Calle y Altura / Dirección</Form.Label>
                    <Form.Control
                      type="text"
                      name="d_domic"
                      value={formData.d_domic}
                      onChange={handleChange}
                      placeholder="Ej: Vicente Damonte 1237"
                    />
                  </Form.Group>
                </div>

                {/* Localidad */}
                <div className="col-12 col-md-4">
                  <Form.Group controlId="profileLocal">
                    <Form.Label className="small fw-bold text-muted mb-1">Localidad / Ciudad</Form.Label>
                    <Form.Control
                      type="text"
                      name="d_local"
                      value={formData.d_local}
                      onChange={handleChange}
                      placeholder="Ej: Lanús Este"
                    />
                  </Form.Group>
                </div>

                {/* Código Postal */}
                <div className="col-12 col-md-3">
                  <Form.Group controlId="profilePosta">
                    <Form.Label className="small fw-bold text-muted mb-1">Código Postal</Form.Label>
                    <Form.Control
                      type="text"
                      name="c_posta"
                      value={formData.c_posta}
                      onChange={handleChange}
                      placeholder="Ej: B1824ALG"
                    />
                  </Form.Group>
                </div>
              </div>

              {/* SECCIÓN 3: SEGURIDAD / CONTRASEÑA */}
              <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 d-flex align-items-center gap-2">
                <i className="bi bi-shield-lock-fill text-warning"></i>
                <span>Seguridad y Contraseña</span>
              </h6>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <Form.Group controlId="profilePass">
                    <Form.Label className="small fw-bold text-muted mb-1">Cambiar Contraseña Web</Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="d_passnu"
                        value={formData.d_passnu}
                        onChange={handleChange}
                        placeholder="Nueva contraseña (dejar como está para conservar)"
                        className="font-monospace"
                      />
                      <Button
                        variant="outline-secondary"
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                      >
                        <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                      </Button>
                    </div>
                    <Form.Text className="text-muted text-2xs">
                      Podés cambiar tu clave de acceso en cualquier momento.
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-light border-0 py-3 px-4">
          <Button
            variant="outline-secondary"
            className="rounded-pill px-4"
            onClick={onHide}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="rounded-pill px-4 fw-bold text-white shadow-2xs d-inline-flex align-items-center gap-2"
            disabled={isSaving || isLoading}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" />
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <i className="bi bi-check2-circle fs-5"></i>
                <span>Guardar Cambios</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CustomerProfileModal;
